import { useState } from 'react';
import { useSession } from '@/services/auth';
import { useUIStore } from '@/stores/uiStore';
import { useToast } from '@/components/ui/Toast/ToastProvider';
import { useUsers, useUpdateUser, useRemoveUser } from '@/services/users';
import { Button } from '@/components/ui/Button/Button';
import { Dialog } from '@/components/ui/Dialog/Dialog';
import { Icon } from '@/components/ui/Icon/Icon';
import { RolePill } from '../RolePill';
import { InviteUserModal } from '../InviteUserModal';
import { nb } from '@/i18n/nb';
import type { Role, User } from '@/types/api';
import s from '../admin.module.css';

const ROLE_OPTIONS: Role[] = ['registered_guest', 'owner', 'admin'];

export function AdminUsers() {
  const { user: actor } = useSession();
  const editMode = useUIStore((state) => state.editMode);
  const toast = useToast();
  const { data: users = [], isLoading } = useUsers();
  const updateUser = useUpdateUser();
  const removeUser = useRemoveUser();
  const [invite, setInvite] = useState(false);
  const [confirm, setConfirm] = useState<User | null>(null);

  const isAdmin = actor?.role === 'admin';

  // Role changes are admin-only; owners may remove only registered guests. The server
  // enforces the same (and the sole-admin 409) — this just gates the affordances.
  function canRemove(target: User): boolean {
    if (!actor || target.id === actor.id) return false;
    if (actor.role === 'admin') return true;
    return actor.role === 'owner' && target.role === 'registered_guest';
  }

  function changeRole(target: User, role: Role) {
    updateUser.mutate(
      { id: target.id, role },
      {
        onSuccess: () =>
          toast({ title: nb.admin.toast.roleChangedTitle, body: nb.admin.toast.roleChangedBody }),
        onError: (error) => toastError(error),
      },
    );
  }

  function remove(target: User) {
    setConfirm(null);
    removeUser.mutate(target.id, {
      onSuccess: () =>
        toast({
          title: nb.admin.toast.removedTitle,
          body: nb.admin.toast.removedBody.replace('{navn}', target.displayName),
        }),
      onError: (error) => toastError(error),
    });
  }

  function toastError(error: unknown) {
    toast({
      title: nb.admin.toast.errorTitle,
      body: error instanceof Error ? error.message : undefined,
      error: true,
    });
  }

  return (
    <div>
      <div className={s.head}>
        <div>
          <h2 className={s.title}>{nb.admin.users.title}</h2>
          <p className={s.lede}>{nb.admin.users.lede}</p>
        </div>
        <Button variant="primary" onClick={() => setInvite(true)}>
          <Icon name="plus" size={18} /> {nb.admin.users.add}
        </Button>
      </div>

      <div className={s.table} role="table">
        <div className={`${s.tr} ${s.trHead}`} role="row">
          <span role="columnheader">{nb.admin.users.colName}</span>
          <span role="columnheader">{nb.admin.users.colEmail}</span>
          <span role="columnheader">{nb.admin.users.colRole}</span>
          <span role="columnheader">{nb.admin.users.colHost}</span>
          <span role="columnheader">{nb.admin.users.colLastLogin}</span>
          <span role="columnheader" aria-label={nb.admin.users.colActions} />
        </div>
        {users.map((u) => (
          <div key={u.id} className={s.tr} role="row">
            <span role="cell" className={s.cellName}>
              {u.displayName}
            </span>
            <span role="cell" className={s.cellMuted}>
              {u.email}
            </span>
            <span role="cell">
              {editMode && isAdmin ? (
                <select
                  className={s.select}
                  value={u.role}
                  aria-label={nb.admin.users.colRole}
                  onChange={(e) => changeRole(u, e.target.value as Role)}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {nb.roles[r]}
                    </option>
                  ))}
                </select>
              ) : (
                <RolePill role={u.role} />
              )}
            </span>
            <span role="cell" className={s.cellMuted}>
              {u.host ?? '—'}
            </span>
            <span role="cell" className={s.cellMuted}>
              {formatLastLogin(u.lastLoginAt)}
            </span>
            <span role="cell" className={s.rowActs}>
              {editMode && canRemove(u) && (
                <button
                  type="button"
                  className={`${s.editBtn} ${s.editBtnDanger}`}
                  onClick={() => setConfirm(u)}
                >
                  <Icon name="trash" size={14} /> {nb.admin.users.remove}
                </button>
              )}
            </span>
          </div>
        ))}
      </div>

      {!editMode && <p className={s.hint}>{nb.admin.users.editHint}</p>}
      {!isLoading && users.length === 0 && <p className={s.hint}>{nb.admin.users.empty}</p>}

      <InviteUserModal open={invite} onClose={() => setInvite(false)} />

      <Dialog
        open={confirm !== null}
        title={nb.admin.removeDialog.title.replace('{navn}', confirm?.displayName ?? '')}
        message={nb.admin.removeDialog.body}
        confirmLabel={nb.admin.removeDialog.confirm}
        confirmVariant="danger"
        onConfirm={() => confirm && remove(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function formatLastLogin(iso: string | null): string {
  if (!iso) return nb.admin.users.neverLoggedIn;
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
}
